/* global axios */
import { DataManager } from '../helper/CacheHelper/DataManager';
import ApiClient from './ApiClient';

// At bootstrap the inbox, label and team stores each call getFromCache() in
// parallel, and every call independently hits
// `/api/v1/accounts/:id/cache_keys`. A single response already carries the keys
// for all three models, so those concurrent calls should share one request
// instead of firing three separate round-trips. We coalesce in-flight requests
// per account and keep the resolved response for a short window so slightly
// staggered callers reuse it too (the endpoint is server-cached for 10s).
const CACHE_KEYS_DEDUP_WINDOW = 3000;
const cacheKeysRequests = new Map();

export const fetchAccountCacheKeys = accountId => {
  const cached = cacheKeysRequests.get(accountId);
  if (cached && Date.now() - cached.timestamp < CACHE_KEYS_DEDUP_WINDOW) {
    return cached.promise;
  }

  const promise = axios
    .get(`/api/v1/accounts/${accountId}/cache_keys`)
    .catch(error => {
      // Don't reuse a failed request — let the next caller retry.
      cacheKeysRequests.delete(accountId);
      throw error;
    });

  cacheKeysRequests.set(accountId, { promise, timestamp: Date.now() });
  return promise;
};

class CacheEnabledApiClient extends ApiClient {
  constructor(resource, options = {}) {
    super(resource, options);
    this.dataManager = new DataManager(this.accountIdFromRoute);
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    throw new Error('cacheModelName is not defined');
  }

  get(cache = false) {
    if (cache) {
      return this.getFromCache();
    }

    return this.getFromNetwork();
  }

  getFromNetwork() {
    return axios.get(this.url);
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data.payload;
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: { payload: dataToParse } };
  }

  async getFromCache() {
    try {
      // IDB is not supported in Firefox private mode: https://bugzilla.mozilla.org/show_bug.cgi?id=781982
      await this.dataManager.initDb();
    } catch {
      return this.getFromNetwork();
    }

    const { data } = await fetchAccountCacheKeys(this.accountIdFromRoute);
    const cacheKeyFromApi = data.cache_keys[this.cacheModelName];
    const isCacheValid = await this.validateCacheKey(cacheKeyFromApi);

    let localData = [];
    if (isCacheValid) {
      localData = await this.dataManager.get({
        modelName: this.cacheModelName,
      });
    }

    if (localData.length === 0) {
      return this.refetchAndCommit(cacheKeyFromApi);
    }

    return this.marshallData(localData);
  }

  async refetchAndCommit(newKey = null) {
    const response = await this.getFromNetwork();

    try {
      await this.dataManager.initDb();

      this.dataManager.replace({
        modelName: this.cacheModelName,
        data: this.extractDataFromResponse(response),
      });

      await this.dataManager.setCacheKeys({
        [this.cacheModelName]: newKey,
      });
    } catch {
      // Ignore error
    }

    return response;
  }

  async validateCacheKey(cacheKeyFromApi) {
    if (!this.dataManager.db) {
      await this.dataManager.initDb();
    }

    const cachekey = await this.dataManager.getCacheKey(this.cacheModelName);
    return cacheKeyFromApi === cachekey;
  }
}

export default CacheEnabledApiClient;
